import { LayoutGrid, Plus, Trash2 } from "lucide-react";
import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ApiError, useCreateSchema, useUpdateSchema } from "@noa/api-client";
import type { ChunkingConfig, DocumentTypeSchema, SchemaCreateRequest, SchemaPatchRequest } from "@noa/api-core";
import { BASE_FIELDS, schemaDialogProps, type SchemaForm, type SchemaTemplate } from "~/utils/schema-admin";
import {
  EMPTY_FIELD,
  EMPTY_SCHEMA_FORM,
  initialFormValues,
  nullSafeResolver,
  toFieldDefinitions,
} from "~/utils/schema-form-utils";
import { SchemaFieldPanel } from "./schema-field-panel";
import { SchemaGeneralSection } from "./schema-general-section";
import { Button, Collapse, Dialog, Form, message, Tag, Text, Title } from "~/components/ui";

type SchemaEditModalProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  schema: DocumentTypeSchema | null;
  mode: "create" | "edit";
  template?: SchemaTemplate | null;
};

export function SchemaEditModal({ isOpen, setIsOpen, schema, mode, template }: SchemaEditModalProps) {
  const { t } = useTranslation("admin");
  const isEdit = mode === "edit";

  const { mutate: createSchema, isPending: isCreating } = useCreateSchema();
  const { mutate: updateSchema, isPending: isUpdating } = useUpdateSchema(schema?.schema_id ?? "");

  const formMethods = useForm<SchemaForm>({
    resolver: nullSafeResolver,
    defaultValues: EMPTY_SCHEMA_FORM,
  });

  const { control, handleSubmit, reset, watch } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "fields",
  });

  const [activeFieldKey, setActiveFieldKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) return;
    const values = initialFormValues(isEdit, schema, template);
    if (values) reset(values);
  }, [isOpen, schema, isEdit, template, reset]);

  const onSubmit = useCallback(
    (values: SchemaForm) => {
      const fields = toFieldDefinitions(values.fields);
      const chunking: ChunkingConfig | undefined = values.chunking?.strategy
        ? (values.chunking as ChunkingConfig)
        : undefined;

      if (isEdit) {
        const updatePayload: SchemaPatchRequest = {
          description: values.description,
          prompt_hint: values.prompt_hint || null,
          version: values.version,
          parser: values.parser || null,
          extract_page_images: values.extract_page_images,
          chunking,
          fields,
        };
        updateSchema(updatePayload, {
          onSuccess: () => {
            message.success(t("schemaAdmin.messages.saved"));
            setIsOpen(false);
            reset();
          },
          onError: (err) => {
            // Optimistic-concurrency conflict (expected_updated_at mismatch):
            // the schema changed under the admin — a generic "save failed"
            // gives no way to understand or recover
            if (err instanceof ApiError && err.status === 409) {
              message.error(t("schemaAdmin.messages.saveConflict"));
            } else {
              message.error(t("schemaAdmin.messages.saveError"));
            }
          },
        });
      } else {
        const createPayload: SchemaCreateRequest = {
          schema_id: values.schema_id,
          doc_type: values.doc_type,
          description: values.description,
          prompt_hint: values.prompt_hint || null,
          version: values.version,
          parser: values.parser || null,
          extract_page_images: values.extract_page_images,
          chunking,
          fields,
        };
        createSchema(createPayload, {
          onSuccess: () => {
            message.success(t("schemaAdmin.messages.created"));
            setIsOpen(false);
            reset();
          },
          onError: (err) => {
            if (err instanceof ApiError && err.status === 409) {
              message.error(t("schemaAdmin.messages.createConflict", { id: values.schema_id }));
            } else {
              message.error(t("schemaAdmin.messages.createError"));
            }
          },
        });
      }
    },
    [isEdit, createSchema, updateSchema, setIsOpen, reset, t]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [setIsOpen, reset]);

  // Without this, a validation failure in a collapsed field panel made the
  // submit button appear to do nothing
  const onInvalid = useCallback(
    (formErrors: FieldErrors<SchemaForm>) => {
      const fieldErrors = formErrors.fields;
      if (Array.isArray(fieldErrors)) {
        const firstIndex = fieldErrors.findIndex(Boolean);
        if (firstIndex >= 0 && fields[firstIndex]) setActiveFieldKey(fields[firstIndex].id);
      }
      message.error(t("schemaAdmin.messages.validationError"));
    },
    [t, fields]
  );

  // Panels are keyed by useFieldArray's stable id (index keys made a deleted
  // field's panel state leak onto its successor). append() doesn't return the
  // new id, so opening the added panel is deferred to the next render.
  const openLastAdded = useRef(false);
  useEffect(() => {
    if (openLastAdded.current && fields.length > 0) {
      openLastAdded.current = false;
      setActiveFieldKey(fields[fields.length - 1].id);
    }
  }, [fields]);

  const handleAddField = useCallback(() => {
    openLastAdded.current = true;
    append(EMPTY_FIELD as SchemaForm["fields"][number]);
  }, [append]);

  const handleAddBaseFields = useCallback(() => {
    const existingNames = new Set(fields.map((f) => f.name));
    const newFields = BASE_FIELDS.filter((f) => !existingNames.has(f.name));
    newFields.forEach((f) => append(f as SchemaForm["fields"][number]));
  }, [append, fields]);

  const modalTitle = isEdit
    ? t("schemaAdmin.modal.editTitle")
    : template
      ? `${t("schemaAdmin.modal.createTitle")} — ${t(`templates.${template.id}.name`)}`
      : t("schemaAdmin.modal.createTitle");

  return (
    <Dialog
      title={modalTitle}
      open={isOpen}
      onOk={handleSubmit(onSubmit, onInvalid)}
      onCancel={handleCancel}
      okText={isEdit ? t("schemaAdmin.actions.save") : t("schemaAdmin.actions.create")}
      cancelText={t("common:cancel")}
      confirmLoading={isCreating || isUpdating}
      {...schemaDialogProps}
      className={cn(schemaDialogProps.className, "max-w-[1200px]")}
      width="90%"
    >
      <FormProvider {...formMethods}>
        <Form className="mt-4">
          <SchemaGeneralSection isEdit={isEdit} />

          <div className="border-t border-noa pt-4 mt-4">
            <div className="flex justify-between items-center mb-3">
              <Title level={5} className="m-0">
                {t("schemaAdmin.fieldsList")} ({fields.length})
              </Title>
              <div className="inline-flex items-center gap-2">
                <Button icon={<LayoutGrid size={14} />} onClick={handleAddBaseFields}>
                  {t("schemaAdmin.addBaseFields")}
                </Button>
                <Button variant="dashed" icon={<Plus size={14} />} onClick={handleAddField}>
                  {t("schemaAdmin.addField")}
                </Button>
              </div>
            </div>

            {fields.length === 0 ? (
              <Text tone="muted" className="block text-center py-4">
                {t("schemaAdmin.noFields")}
              </Text>
            ) : (
              <Collapse
                accordion
                // `[]`, not undefined: undefined would switch the Collapse to uncontrolled
                activeKey={activeFieldKey ?? []}
                onChange={(keys) => setActiveFieldKey(keys[0])}
                items={fields.map((_field, index) => ({
                  key: _field.id,
                  label: (
                    <div className="inline-flex items-center gap-2">
                      <Text strong className="font-mono">
                        {watch(`fields.${index}.name`) || `field_${index + 1}`}
                      </Text>
                      {watch(`fields.${index}.type`) && (
                        <Tag color="blue" className="text-xs">
                          {watch(`fields.${index}.type`)}
                        </Tag>
                      )}
                      {watch(`fields.${index}.required`) && (
                        <Tag color="red" className="text-xs">
                          {t("schemaAdmin.field.required")}
                        </Tag>
                      )}
                    </div>
                  ),
                  extra: (
                    <Button
                      variant="text"
                      danger
                      size="small"
                      icon={<Trash2 size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    />
                  ),
                  children: <SchemaFieldPanel index={index} autoFocusName={activeFieldKey === _field.id} />,
                }))}
              />
            )}
          </div>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
